import { extra } from "./extra";

export const translations = {
  en: {
    nav: { platform: "Platform", architecture: "Architecture", benchmark: "Benchmark", security: "Security", team: "Team" },
    bookDemo: "Book a demo",
    hero: {
      eyebrow: "Private Industrial AI for Enterprise Manufacturing",
      line1: "Assign a task.",
      line2: "Agents do the work.",
      line3: "Every insight traced.",
      sub: "Streamline360 is the agentic AI platform purpose-built for enterprise manufacturing. Ask in plain operational language — specialised agents pull live plant context from your Knowledge Graph and return answers in seconds. On-premise and at the edge: your data never leaves the plant.",
      ctaBenchmark: "See the benchmark",
      stats: [
        { value: "90.2%", label: "Benchmark accuracy" },
        { value: "~$0.05", label: "Cost per task" },
        { value: "Seconds", label: "Time to insight" },
        { value: "100%", label: "Traceable to source" },
      ],
    },
    marquee: ["ZERO HALLUCINATIONS", "100% TRACEABLE", "KNOWLEDGE GRAPH GROUNDED", "ON-PREMISE & EDGE", "PLAIN-LANGUAGE QUERIES", "OPC-UA · MQTT · HISTORIAN APIs"],
    problem: {
      tag: "The Problem",
      h2: "Your plant still can't use the power of language models today.",
      sub: "Global manufacturing loses billions to unplanned downtime — yet generic AI stays locked out of the factory floor for three structural reasons.",
      cards: [
        { title: "The Data Sovereignty Wall", body: "Security and critical-infrastructure rules strictly prohibit sending proprietary telemetry and process recipes outside the plant perimeter. Cloud AI is a non-starter." },
        { title: "Legacy Data Silos", body: "Crucial factory intelligence stays trapped across disjointed historians, legacy SCADA systems, control networks and physical shift logs." },
        { title: "The Pilot Trap", body: "Generic frontier models are too slow, too expensive to query continuously, and prone to hallucinations during critical production events." },
      ],
    },
    stepper: {
      tag: "From Task to Result",
      h2: "You assign a task. Agents take it from there.",
      steps: [
        { n: "01", label: "Assign a task", quote: "\u201CWhy did Line 3 stop yesterday?\u201D", body: "Plant staff ask in standard operational language. No queries to write, no dashboards to configure, no data to export." },
        { n: "02", label: "Agents pull context", body: "Specialised agents retrieve plant data and relationships from the Knowledge Graph — only what the task needs, nothing more." },
        { n: "03", label: "Agents analyse", body: "Downtime, alarms, parameters and trends are analysed against verified plant context — never raw text guesswork." },
        { n: "04", label: "Agents produce output", body: "A dashboard is built, a root cause is identified, recommendations are formulated — each backed by evidence." },
        { n: "05", label: "You get the result", body: "Insights, recommendations and root causes delivered in seconds — every one traceable to its source data, end to end." },
      ],
    },
    arch: {
      tag: "The Architecture",
      h2: "One Knowledge Graph. A data-blind boundary.",
      sub: "The local engine links live machine telemetry with your plant's operating knowledge inside a secure perimeter. Raw data stays in — only abstracted context moves.",
      tour: [
        { label: "Step 1 — Your data flows in", text: "Seven streams of plant intelligence — historians, control systems, MES, maintenance, lab, documents, ERP — flow in as raw telemetry." },
        { label: "Step 2 — The data-blind boundary", text: "At the perimeter, raw payloads are abstracted into operational metadata. Nothing raw ever crosses." },
        { label: "Step 3 — The Knowledge Graph", text: "Machine telemetry is fused with human operating knowledge — SOPs, shift logs, plant rules — computed entirely on-premise." },
        { label: "Step 4 — The Orchestration Engine", text: "The proprietary orchestration engine interprets your task in operational language and retrieves only the context it needs." },
        { label: "Step 5 — Trusted outputs", text: "Dashboards, root causes, alarm insights and recommendations flow back — every one traceable to source." },
        { label: "Step 6 — The cloud stays out", text: "The path to frontier cloud AI is blocked by architecture, not by policy. Your telemetry never leaves the plant." },
      ],
      legend: ["Raw telemetry — stays inside the plant", "Abstracted metadata — agents reason over this", "Cloud exfiltration — blocked"],
      defaultDesc: "Hover a data source, the boundary, or the Knowledge Graph to trace how data flows — and what never leaves.",
      replay: "Replay tour",
      connectors: "Connectors",
      yourData: "Your data",
      runsLocally: "RUNS LOCALLY",
      blocked: "BLOCKED",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Dual-stream context behind a data-blind boundary: live telemetry fused with SOPs, shift logs and plant rules. Raw data never leaves.",
      agentsTitle: "Proprietary Orchestration Engine",
      outputsTitle: "Trusted Outputs",
      descs: {
        hub: "Dual-stream context: live machine telemetry fused with your plant's operating knowledge — computed entirely on-premise.",
        boundary: "Raw payloads are abstracted into operational metadata at the perimeter. Models run locally — nothing is exfiltrated to the cloud.",
        agents: "The proprietary orchestration engine interprets operational intent, retrieves only the relevant context, and assembles answers with a full source trace.",
        cloud: "Generic cloud AI would require streaming raw telemetry off-site. The boundary blocks it — by architecture, not by policy.",
      },
      sourceDescs: [
        "Time-series process trends stream in as raw telemetry — only abstracted operational metadata ever crosses the boundary.",
        "Live control signals and alarms stay on your control network. Agents reason over context, never raw payloads.",
        "Production orders, OEE events and shop-floor execution data stream in from the MES layer.",
        "Work orders and maintenance history ground every root-cause analysis in what was actually done on the floor.",
        "Lab results fuse with batch context so quality deviations correlate directly with process conditions.",
        "Operating manuals and plant rules bound every recommendation — nothing exceeds your pre-validated parameters.",
        "Orders, schedules and inventory connect production context to business priorities.",
      ],
      outputDescs: [
        "Live operational dashboards, built on request from plant data.",
        "Deviations and stops traced to their specific cause, with evidence.",
        "What an alarm means, why it fired, and what to do next.",
        "Actionable next steps, bound to validated operating parameters.",
      ],
    },
    deliver: {
      tag: "What You Get",
      h2: "Answers, not raw text.",
      liveExample: "Live example — \u201CBuild an OEE dashboard for the press shop.\u201D",
      cards: [
        { title: "Dashboards", body: "Built on request, sourced from live plant data — OEE, downtime, quality, energy." },
        { title: "Root-Cause Analysis", body: "The specific reason behind a deviation or stop, identified and evidenced." },
        { title: "Alarm Insights", body: "What an alarm means, why it fired, and what to do about it." },
        { title: "Recommendations", body: "Actionable next steps — every one bound to pre-validated operating parameters." },
      ],
    },
    bench: {
      tag: "The Benchmark",
      h2: "Outperforms frontier AI. On your data.",
      sub: "Same tasks, same factory datasets, side by side — scored by independent human process engineers. Focused retrieval beats raw text processing on every metric.",
      stats: [
        { label: "Accuracy", sub: "vs 15.8–17.7% for frontier models" },
        { label: "Cost per task", sub: "vs up to ~$1.80 per task" },
        { label: "Tokens per task", sub: "vs 500k+ for frontier models" },
      ],
      theirs: "Frontier AI models",
      bars: ["Accuracy", "Cost per task", "Tokens per task"],
      footnote: "Benchmark, August 2026 · identical factory datasets · human-verified scoring. Lower cost and token bars are better.",
    },
    security: {
      tag: "Security & Deployment",
      h2: "Your telemetry never leaves the plant.",
      sub: "Streamline360 is privacy-first by architecture, not by policy. The data-blind boundary keeps proprietary recipes and process data inside your secure perimeter.",
      cards: [
        { title: "On-Premise & Edge", body: "Installs directly at the edge or inside your plant network. No cloud dependency, no latency barrier." },
        { title: "Local Data Boundary", body: "Raw telemetry is abstracted into operational metadata. Models run locally — nothing is exfiltrated." },
        { title: "Zero Hallucinations", body: "Recommendations are strictly bound by your facility operating manuals and pre-validated parameters." },
        { title: "Complete Auditability", body: "Every insight links to specific sensor tags, physical timestamps and approved procedures." },
      ],
      deployLabel: "Deployment",
      deployCaption: "Inside your perimeter. Under your control.",
      compliance: "Compliance roadmap",
    },
    team: {
      tag: "Leadership",
      h2: "Built by people who know the plant floor.",
      sub: "A founding team spanning industrial AI, enterprise software and manufacturing operations — obsessed with making intelligence safe for critical infrastructure.",
      members: [
        { role: "Co-Founder & Chief Executive", bio: "Enterprise software and industrial go-to-market. Leads strategy, partnerships and the OEM channel network." },
        { role: "Co-Founder & Chief Technology", bio: "Industrial AI and edge systems. Architected the Knowledge Graph engine and the data-blind boundary." },
        { role: "Head of Product", bio: "Manufacturing operations and HMI design. Owns the plain-language agent experience on the plant floor." },
      ],
      onRequest: "Profile on request",
    },
    contact: {
      tag: "Get Started",
      h2: "See it on your data.",
      sub: "Book a technical demo and we'll run Streamline360 against a sample of your plant data — on-premise, inside your perimeter.",
      bullets: ["60-day technical validation path", "Deploys at the edge or on-premise", "Connects to historians, SCADA, CMMS out of the box"],
      name: "Full name",
      company: "Company",
      email: "Work email",
      message: "What would you like Streamline360 to analyse? (optional)",
      submit: "Request a demo",
      toastOk: "Request received. Our team will reach out shortly.",
      toastErr: "Something went wrong. Please try again.",
      note: "Your details stay with us. No data leaves your plant during a demo.",
    },
    footer: {
      tagline: "Private, on-premise agentic AI for enterprise manufacturing.",
      impressum: "Impressum",
      madeIn: "Private, on-premise agentic AI for enterprise manufacturing — made in Copenhagen.",
      rights: "© 2026 Streamline360 ApS. All rights reserved.",
      neverLeaves: "Your data never leaves the plant.",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      contact: "Contact",
    },
  },
  de: {
    nav: { platform: "Plattform", architecture: "Architektur", benchmark: "Benchmark", security: "Sicherheit", team: "Team" },
    bookDemo: "Demo buchen",
    hero: {
      eyebrow: "Private Industrial AI für die industrielle Fertigung",
      line1: "Aufgabe stellen.",
      line2: "Agenten erledigen den Rest.",
      line3: "Jede Erkenntnis rückverfolgbar.",
      sub: "Streamline360 ist die agentische KI-Plattform für die industrielle Fertigung. Fragen Sie in einfacher Betriebssprache — spezialisierte Agenten holen live Anlagenkontext aus Ihrem Knowledge Graph und liefern Antworten in Sekunden. On-Premise und am Edge: Ihre Daten verlassen nie das Werk.",
      ctaBenchmark: "Benchmark ansehen",
      stats: [
        { value: "90,2 %", label: "Benchmark-Genauigkeit" },
        { value: "~0,05 $", label: "Kosten pro Aufgabe" },
        { value: "Sekunden", label: "Zeit bis zur Erkenntnis" },
        { value: "100 %", label: "Rückverfolgbar zur Quelle" },
      ],
    },
    marquee: ["KEINE HALLUZINATIONEN", "100 % RÜCKVERFOLGBAR", "KNOWLEDGE-GRAPH-GESTÜTZT", "ON-PREMISE & EDGE", "ABFRAGEN IN ALLTAGSSPRACHE", "OPC-UA · MQTT · HISTORIAN-APIS"],
    problem: {
      tag: "Das Problem",
      h2: "Ihr Werk kann die Leistungsfähigkeit von Sprachmodellen heute noch nicht nutzen.",
      sub: "Die globale Fertigung verliert Milliarden durch ungeplante Stillstände — dennoch bleibt generische KI aus drei strukturellen Gründen aus der Fabrikhalle ausgesperrt.",
      cards: [
        { title: "Die Datenhoheits-Mauer", body: "Sicherheits- und Critical-Infrastructure-Vorgaben verbieten strikt, proprietäre Telemetrie und Prozessrezepte außerhalb des Werksperimeters zu senden. Cloud-KI ist keine Option." },
        { title: "Legacy-Datensilos", body: "Entscheidendes Fabrikwissen bleibt über getrennte Historians, Legacy-SCADA-Systeme, Steuerungsnetze und physische Schichtbücher verstreut." },
        { title: "Die Pilot-Falle", body: "Generische Frontier-Modelle sind zu langsam, zu teuer für kontinuierliche Abfragen und neigen bei kritischen Produktionsereignissen zu Halluzinationen." },
      ],
    },
    stepper: {
      tag: "Von der Aufgabe zum Ergebnis",
      h2: "Sie stellen eine Aufgabe. Agenten übernehmen den Rest.",
      steps: [
        { n: "01", label: "Aufgabe stellen", quote: "\u201EWarum ist Linie 3 gestern stehen geblieben?\u201C", body: "Anlagenmitarbeiter fragen in gewohnter Betriebssprache. Keine Abfragen schreiben, keine Dashboards konfigurieren, keine Daten exportieren." },
        { n: "02", label: "Agenten holen Kontext", body: "Spezialisierte Agenten rufen Anlagendaten und Zusammenhänge aus dem Knowledge Graph ab — nur was die Aufgabe braucht, nichts mehr." },
        { n: "03", label: "Agenten analysieren", body: "Stillstände, Alarme, Parameter und Trends werden gegen verifizierten Anlagenkontext analysiert — niemals als Rohtext-Raten." },
        { n: "04", label: "Agenten liefern Ergebnisse", body: "Ein Dashboard wird erstellt, eine Ursache identifiziert, Empfehlungen formuliert — jede mit Belegen." },
        { n: "05", label: "Sie erhalten das Ergebnis", body: "Erkenntnisse, Empfehlungen und Ursachen in Sekunden — jede einzelne lückenlos zur Quelle rückverfolgbar." },
      ],
    },
    arch: {
      tag: "Die Architektur",
      h2: "Ein Knowledge Graph. Eine datenblinde Grenze.",
      sub: "Die lokale Engine verknüpft live Maschinentelemetrie mit dem Betriebswissen Ihres Werks innerhalb eines sicheren Perimeters. Rohdaten bleiben drin — nur abstrahierter Kontext bewegt sich.",
      tour: [
        { label: "Schritt 1 — Ihre Daten fließen ein", text: "Sieben Ströme von Anlagenintelligenz — Historians, Leitsysteme, MES, Instandhaltung, Labor, Dokumente, ERP — fließen als Roh-Telemetrie ein." },
        { label: "Schritt 2 — Die datenblinde Grenze", text: "Am Perimeter werden Rohdaten in betriebliche Metadaten abstrahiert. Nichts Rohes passiert die Grenze." },
        { label: "Schritt 3 — Der Knowledge Graph", text: "Maschinentelemetrie wird mit menschlichem Betriebswissen verschmolzen — SOPs, Schichtbücher, Anlagenregeln — vollständig on-premise berechnet." },
        { label: "Schritt 4 — Spezialisierte Agenten", text: "Agenten interpretieren Ihre Aufgabe in Betriebssprache und rufen nur den benötigten Kontext ab." },
        { label: "Schritt 5 — Vertrauenswürdige Ergebnisse", text: "Dashboards, Ursachen, Alarm-Insights und Empfehlungen fließen zurück — jede rückverfolgbar zur Quelle." },
        { label: "Schritt 6 — Die Cloud bleibt draußen", text: "Der Pfad zu Frontier-Cloud-KI ist architektonisch blockiert, nicht per Richtlinie. Ihre Telemetrie verlässt nie das Werk." },
      ],
      legend: ["Roh-Telemetrie — bleibt im Werk", "Abstrahierte Metadaten — Grundlage der Agenten", "Cloud-Exfiltration — blockiert"],
      defaultDesc: "Fahren Sie mit der Maus über eine Datenquelle, die Grenze oder den Knowledge Graph, um den Datenfluss zu verfolgen — und was niemals abfließt.",
      replay: "Tour erneut abspielen",
      connectors: "Konnektoren",
      yourData: "Ihre Daten",
      runsLocally: "LÄUFT LOKAL",
      blocked: "BLOCKIERT",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Dual-Stream-Kontext hinter einer datenblinden Grenze: live Telemetrie verschmolzen mit SOPs, Schichtbüchern und Anlagenregeln. Rohdaten verlassen das Werk nie.",
      agentsTitle: "Spezialisierte Agenten",
      outputsTitle: "Vertrauenswürdige Ergebnisse",
      descs: {
        hub: "Dual-Stream-Kontext: live Maschinentelemetrie verschmolzen mit dem Betriebswissen Ihres Werks — vollständig on-premise berechnet.",
        boundary: "Rohdaten werden am Perimeter in betriebliche Metadaten abstrahiert. Modelle laufen lokal — nichts wird in die Cloud exfiltriert.",
        agents: "Spezialisierte Agenten interpretieren betriebliche Absicht, rufen nur relevanten Kontext ab und erstellen Antworten mit vollständiger Quellenspur.",
        cloud: "Generische Cloud-KI würde erfordern, Rohtelemetrie extern zu streamen. Die Grenze blockiert das — per Architektur, nicht per Richtlinie.",
      },
      sourceDescs: [
        "Zeitreihen-Prozesstrends strömen als Roh-Telemetrie ein — nur abstrahierte betriebliche Metadaten passieren die Grenze.",
        "Live Steuersignale und Alarme bleiben in Ihrem Leitnetz. Agenten schlussfolgern über Kontext, niemals über Rohdaten.",
        "Fertigungsaufträge, OEE-Ereignisse und Shopfloor-Daten strömen aus der MES-Ebene ein.",
        "Arbeitsaufträge und Wartungshistorie verankern jede Ursachenanalyse in dem, was tatsächlich am Shopfloor getan wurde.",
        "Laborergebnisse verschmelzen mit Chargenkontext, sodass Qualitätsabweichungen direkt mit Prozessbedingungen korrelieren.",
        "Betriebshandbücher und Anlagenregeln begrenzen jede Empfehlung — nichts überschreitet Ihre vorvalidierten Parameter.",
        "Aufträge, Zeitpläne und Bestände verbinden Produktionskontext mit Geschäftsprioritäten.",
      ],
      outputDescs: [
        "Live Betriebs-Dashboards, auf Anfrage aus Anlagendaten erstellt.",
        "Abweichungen und Stillstände bis zur konkreten Ursache zurückverfolgt, mit Belegen.",
        "Was ein Alarm bedeutet, warum er ausgelöst hat und was als Nächstes zu tun ist.",
        "Umsetzbare nächste Schritte, an validierte Betriebsparameter gebunden.",
      ],
    },
    deliver: {
      tag: "Was Sie erhalten",
      h2: "Antworten, kein Rohtext.",
      liveExample: "Live-Beispiel — \u201EErstelle ein OEE-Dashboard für die Pressenstraße.\u201C",
      cards: [
        { title: "Dashboards", body: "Auf Anfrage erstellt, aus live Anlagendaten gespeist — OEE, Stillstände, Qualität, Energie." },
        { title: "Ursachenanalyse", body: "Der konkrete Grund hinter einer Abweichung oder einem Stillstand — identifiziert und belegt." },
        { title: "Alarm-Insights", body: "Was ein Alarm bedeutet, warum er ausgelöst hat und was zu tun ist." },
        { title: "Empfehlungen", body: "Umsetzbare nächste Schritte — jede an vorvalidierte Betriebsparameter gebunden." },
      ],
    },
    bench: {
      tag: "Das Benchmark",
      h2: "Übertrifft Frontier-KI. Auf Ihren Daten.",
      sub: "Gleiche Aufgaben, gleiche Fabrikdatensätze, Seite an Seite — bewertet von unabhängigen Verfahrensingenieuren. Fokussierter Abruf schlägt Rohtext-Verarbeitung bei jeder Metrik.",
      stats: [
        { label: "Genauigkeit", sub: "vs 15,8–17,7 % bei Frontier-Modellen" },
        { label: "Kosten pro Aufgabe", sub: "vs bis zu ~1,80 $ pro Aufgabe" },
        { label: "Token pro Aufgabe", sub: "vs 500k+ bei Frontier-Modellen" },
      ],
      theirs: "Frontier-KI-Modelle",
      bars: ["Genauigkeit", "Kosten pro Aufgabe", "Token pro Aufgabe"],
      footnote: "Benchmark, August 2026 · identische Fabrikdatensätze · menschlich verifizierte Bewertung. Niedrigere Kosten- und Token-Balken sind besser.",
    },
    security: {
      tag: "Sicherheit & Deployment",
      h2: "Ihre Telemetrie verlässt nie das Werk.",
      sub: "Streamline360 ist Privacy-First per Architektur, nicht per Richtlinie. Die datenblinde Grenze hält proprietäre Rezepte und Prozessdaten innerhalb Ihres sicheren Perimeters.",
      cards: [
        { title: "On-Premise & Edge", body: "Installiert direkt am Edge oder in Ihrem Werksnetz. Keine Cloud-Abhängigkeit, keine Latenz-Barriere." },
        { title: "Lokale Datengrenze", body: "Rohtelemetrie wird zu betrieblichen Metadaten abstrahiert. Modelle laufen lokal — nichts wird exfiltriert." },
        { title: "Keine Halluzinationen", body: "Empfehlungen sind strikt an Ihre Betriebshandbücher und vorvalidierte Parameter gebunden." },
        { title: "Vollständige Prüfbarkeit", body: "Jede Erkenntnis verweist auf konkrete Sensortags, Zeitstempel und freigegebene Verfahren." },
      ],
      deployLabel: "Deployment",
      deployCaption: "Innerhalb Ihres Perimeters. Unter Ihrer Kontrolle.",
      compliance: "Compliance-Roadmap",
    },
    team: {
      tag: "Führungsteam",
      h2: "Gebaut von Menschen, die die Fabrikhalle kennen.",
      sub: "Ein Gründerteam aus industrieller KI, Enterprise-Software und Fertigungsbetrieb — besessen davon, Intelligenz für kritische Infrastruktur sicher zu machen.",
      members: [
        { role: "Mitgründer & CEO", bio: "Enterprise-Software und industrieller Go-to-Market. Verantwortet Strategie, Partnerschaften und das OEM-Kanalnetzwerk." },
        { role: "Mitgründer & CTO", bio: "Industrielle KI und Edge-Systeme. Architekt der Knowledge-Graph-Engine und der datenblinden Grenze." },
        { role: "Head of Product", bio: "Fertigungsbetrieb und HMI-Design. Verantwortet die Agenten-Erfahrung in Alltagssprache am Shopfloor." },
      ],
      onRequest: "Profil auf Anfrage",
    },
    contact: {
      tag: "Loslegen",
      h2: "Sehen Sie es auf Ihren Daten.",
      sub: "Buchen Sie eine technische Demo — wir lassen Streamline360 gegen eine Stichprobe Ihrer Anlagendaten laufen, on-premise, innerhalb Ihres Perimeters.",
      bullets: ["60-tägiger technischer Validierungspfad", "Deployment am Edge oder on-premise", "Verbindet Historians, SCADA und CMMS out of the box"],
      name: "Vollständiger Name",
      company: "Unternehmen",
      email: "Geschäftliche E-Mail",
      message: "Was soll Streamline360 analysieren? (optional)",
      submit: "Demo anfordern",
      toastOk: "Anfrage erhalten. Unser Team meldet sich in Kürze.",
      toastErr: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
      note: "Ihre Angaben bleiben bei uns. Während einer Demo verlassen keine Daten Ihr Werk.",
    },
    footer: {
      tagline: "Private, on-premise agentische KI für die industrielle Fertigung.",
      impressum: "Impressum",
      madeIn: "Private, on-premise agentische KI für die industrielle Fertigung — made in Copenhagen.",
      rights: "© 2026 Streamline360 ApS. Alle Rechte vorbehalten.",
      neverLeaves: "Ihre Daten verlassen nie das Werk.",
      privacy: "Datenschutzerklärung",
      terms: "Nutzungsbedingungen",
      contact: "Kontakt",
    },
  },
  ja: {
    nav: { platform: "プラットフォーム", architecture: "アーキテクチャ", benchmark: "ベンチマーク", security: "セキュリティ", team: "チーム" },
    bookDemo: "デモを予約",
    hero: {
      eyebrow: "エンタープライズ製造業向けプライベート産業AI",
      line1: "タスクを指示。",
      line2: "エージェントが実行。",
      line3: "すべての洞察を追跡可能に。",
      sub: "Streamline360は、エンタープライズ製造業向けに構築されたエージェンティックAIプラットフォームです。現場の言葉で尋ねるだけで、専門エージェントがナレッジグラフからライブのプラントコンテキストを取得し、数秒で回答します。オンプレミスとエッジで動作し、データが工場の外に出ることはありません。",
      ctaBenchmark: "ベンチマークを見る",
      stats: [
        { value: "90.2%", label: "ベンチマーク精度" },
        { value: "~$0.05", label: "タスクあたりのコスト" },
        { value: "数秒", label: "洞察までの時間" },
        { value: "100%", label: "ソースまで追跡可能" },
      ],
    },
    marquee: ["ハルシネーションゼロ", "100%追跡可能", "ナレッジグラフ基盤", "オンプレミス＆エッジ", "自然言語クエリ", "OPC-UA · MQTT · ヒストリアンAPI"],
    problem: {
      tag: "課題",
      h2: "あなたのプラントは今日も、言語モデルの力を活用できていません。",
      sub: "世界の製造業は計画外ダウンタイムで数十億ドルを失っています。それでも汎用AIは3つの構造的な理由で工場現場から締め出されています。",
      cards: [
        { title: "データ主権の壁", body: "セキュリティと重要インフラの規則により、独自のテレメトリやプロセスレシピをプラント境界の外に送信することは厳しく禁じられています。クラウドAIは選択肢になりません。" },
        { title: "レガシーデータサイロ", body: "重要な工場インテリジェンスは、分散したヒストリアン、レガシーSCADA、制御ネットワーク、紙のシフトログに閉じ込められたままです。" },
        { title: "パイロットの罠", body: "汎用フロンティアモデルは遅すぎ、継続的なクエリには高すぎ、重要な生産イベントでハルシネーションを起こしがちです。" },
      ],
    },
    stepper: {
      tag: "タスクから結果へ",
      h2: "タスクを指示。あとはエージェントにお任せください。",
      steps: [
        { n: "01", label: "タスクを指示", quote: "「なぜ昨日ライン3は停止したのか？」", body: "プラントスタッフは普段の運用言語で質問するだけ。クエリも、ダッシュボード設定も、データエクスポートも不要です。" },
        { n: "02", label: "エージェントがコンテキスト取得", body: "専門エージェントがナレッジグラフからプラントデータと関係性を取得します。タスクに必要なものだけを。" },
        { n: "03", label: "エージェントが分析", body: "ダウンタイム、アラーム、パラメータ、トレンドを検証済みのプラントコンテキストに照らして分析。生テキストの推測はしません。" },
        { n: "04", label: "エージェントが出力を生成", body: "ダッシュボードが構築され、根本原因が特定され、推奨事項が作成されます。すべて根拠付きです。" },
        { n: "05", label: "結果を受け取る", body: "洞察、推奨、根本原因を数秒で。すべてエンドツーエンドでソースまで追跡可能です。" },
      ],
    },
    arch: {
      tag: "アーキテクチャ",
      h2: "1つのナレッジグラフ。データブラインドな境界。",
      sub: "ローカルエンジンが、ライブの機械テレメトリとプラントの運用知識を安全な境界内で結び付けます。生データは内部に留まり、動くのは抽象化されたコンテキストだけです。",
      tour: [
        { label: "ステップ1 — データの取り込み", text: "7つのプラントインテリジェンスストリーム（ヒストリアン、制御系、MES、保全、ラボ、文書、ERP）が生テレメトリとして流れ込みます。" },
        { label: "ステップ2 — データブラインド境界", text: "境界で生データは運用メタデータに抽象化されます。生データが境界を越えることはありません。" },
        { label: "ステップ3 — ナレッジグラフ", text: "機械テレメトリが人間の運用知識（SOP、シフトログ、プラントルール）と融合。完全にオンプレミスで計算されます。" },
        { label: "ステップ4 — 専門エージェント", text: "エージェントが運用言語でタスクを解釈し、必要なコンテキストのみを取得します。" },
        { label: "ステップ5 — 信頼できる出力", text: "ダッシュボード、根本原因、アラーム洞察、推奨事項が返ってきます。すべてソースまで追跡可能です。" },
        { label: "ステップ6 — クラウドは入れない", text: "フロンティアクラウドAIへの経路は、ポリシーではなくアーキテクチャでブロックされています。テレメトリがプラントの外に出ることはありません。" },
      ],
      legend: ["生テレメトリ — プラント内に保持", "抽象化メタデータ — エージェントの推論対象", "クラウドへの持ち出し — ブロック"],
      defaultDesc: "データソース、境界、ナレッジグラフにホバーして、データの流れと、決して外部に出ないものを確認してください。",
      replay: "ツアーを再生",
      connectors: "コネクタ",
      yourData: "お客様のデータ",
      runsLocally: "ローカルで動作",
      blocked: "ブロック",
      kgMobileTitle: "ナレッジグラフ",
      kgMobileBody: "データブラインド境界の内側で、ライブテレメトリとSOP・シフトログ・プラントルールが融合したデュアルストリームコンテキスト。生データが外部に出ることはありません。",
      agentsTitle: "専門エージェント",
      outputsTitle: "信頼できる出力",
      descs: {
        hub: "デュアルストリームコンテキスト：ライブの機械テレメトリとプラントの運用知識が融合 — 完全にオンプレミスで計算されます。",
        boundary: "生データは境界で運用メタデータに抽象化されます。モデルはローカルで実行され、クラウドへの送信は一切ありません。",
        agents: "専門エージェントが運用上の意図を解釈し、関連コンテキストのみを取得して、完全なソーストレース付きの回答を組み立てます。",
        cloud: "汎用クラウドAIは生テレメトリの外部送信が必要です。境界がそれをブロックします — ポリシーではなくアーキテクチャによって。",
      },
      sourceDescs: [
        "時系列プロセストレンドが生テレメトリとして流れ込み、抽象化された運用メタデータのみが境界を越えます。",
        "ライブの制御信号とアラームは制御ネットワーク内に保持。エージェントは生データではなくコンテキストで推論します。",
        "生産指示、OEEイベント、現場実績データがMES層から流れ込みます。",
        "作業指示書と保全履歴が、現場で実際に行われたことに基づく根本原因分析を支えます。",
        "ラボ結果がバッチコンテキストと融合し、品質偏差がプロセス条件と直接相関します。",
        "運用マニュアルとプラントルールがすべての推奨を拘束。事前検証済みパラメータを超えることはありません。",
        "受注、スケジュール、在庫が生産コンテキストとビジネスの優先事項を結び付けます。",
      ],
      outputDescs: [
        "プラントデータからリクエストに応じて構築されるライブ運用ダッシュボード。",
        "偏差や停止を具体的な原因まで根拠付きで追跡。",
        "アラームの意味、発報理由、次に取るべき対応。",
        "検証済み運用パラメータに紐付いた実行可能な次のステップ。",
      ],
    },
    deliver: {
      tag: "得られるもの",
      h2: "生テキストではなく、回答を。",
      liveExample: "ライブ例 — 「プレスショップのOEEダッシュボードを作成して」",
      cards: [
        { title: "ダッシュボード", body: "リクエストに応じてライブのプラントデータから構築 — OEE、ダウンタイム、品質、エネルギー。" },
        { title: "根本原因分析", body: "偏差や停止の背後にある具体的な原因を特定し、根拠を提示します。" },
        { title: "アラーム洞察", body: "アラームの意味、発報理由、そして次に取るべき対応。" },
        { title: "推奨事項", body: "実行可能な次のステップ。すべて事前検証済みの運用パラメータに紐付いています。" },
      ],
    },
    bench: {
      tag: "ベンチマーク",
      h2: "フロンティアAIを上回る。お客様のデータで。",
      sub: "同じタスク、同じ工場データセットを並べて、独立したプロセスエンジニアが採点。コンテキスト重視の検索がすべての指標で生テキスト処理を上回りました。",
      stats: [
        { label: "精度", sub: "フロンティアモデルは15.8〜17.7%" },
        { label: "タスクあたりのコスト", sub: "最大〜$1.80/タスクとの比較" },
        { label: "タスクあたりのトークン", sub: "フロンティアモデルは500k+" },
      ],
      theirs: "フロンティアAIモデル",
      bars: ["精度", "タスクあたりのコスト", "タスクあたりのトークン"],
      footnote: "ベンチマーク：2026年8月 · 同一の工場データセット · 人間による検証済みスコア。コストとトークンのバーは低いほど優れています。",
    },
    security: {
      tag: "セキュリティと導入",
      h2: "テレメトリはプラントの外に出ません。",
      sub: "Streamline360はポリシーではなくアーキテクチャによるプライバシーファースト。データブラインド境界が、独自のレシピやプロセスデータを安全な境界内に保持します。",
      cards: [
        { title: "オンプレミス＆エッジ", body: "エッジやプラントネットワークに直接インストール。クラウド依存もレイテンシの壁もありません。" },
        { title: "ローカルデータ境界", body: "生テレメトリは運用メタデータに抽象化。モデルはローカルで実行され、外部への送信はありません。" },
        { title: "ハルシネーションゼロ", body: "推奨事項は施設の運用マニュアルと事前検証済みパラメータに厳密に拘束されます。" },
        { title: "完全な監査可能性", body: "すべての洞察が特定のセンサータグ、タイムスタンプ、承認済み手順に紐付いています。" },
      ],
      deployLabel: "導入",
      deployCaption: "お客様の境界内で。お客様の管理下で。",
      compliance: "コンプライアンスロードマップ",
    },
    team: {
      tag: "リーダーシップ",
      h2: "現場を知る人々が作っています。",
      sub: "産業AI、エンタープライズソフトウェア、製造オペレーションにまたがる創業チーム。重要インフラに安全なインテリジェンスを届けることに情熱を注いでいます。",
      members: [
        { role: "共同創業者 & CEO", bio: "エンタープライズソフトウェアと産業向けGo-to-Market。戦略、パートナーシップ、OEMチャネルネットワークを統括。" },
        { role: "共同創業者 & CTO", bio: "産業AIとエッジシステム。ナレッジグラフエンジンとデータブラインド境界の設計者。" },
        { role: "プロダクト責任者", bio: "製造オペレーションとHMIデザイン。プラントフロアでの自然言語エージェント体験を担当。" },
      ],
      onRequest: "プロフィールはお問い合わせください",
    },
    contact: {
      tag: "はじめる",
      h2: "お客様のデータでお試しください。",
      sub: "技術デモをご予約ください。お客様のプラントデータのサンプルに対して、オンプレミス・お客様の境界内でStreamline360を実行します。",
      bullets: ["60日間の技術検証パス", "エッジまたはオンプレミスに導入", "ヒストリアン、SCADA、CMMSに標準で接続"],
      name: "氏名",
      company: "会社名",
      email: "会社のメールアドレス",
      message: "Streamline360に何を分析させますか？（任意）",
      submit: "デモをリクエスト",
      toastOk: "リクエストを受け付けました。担当者よりご連絡いたします。",
      toastErr: "エラーが発生しました。もう一度お試しください。",
      note: "お客様の情報は当社内で管理されます。デモ中もデータがプラントの外に出ることはありません。",
    },
    footer: {
      tagline: "エンタープライズ製造業向けのプライベートなオンプレミス・エージェンティックAI。",
      impressum: "会社情報",
      madeIn: "エンタープライズ製造業向けプライベート産業AI — コペンハーゲン製。",
      rights: "© 2026 Streamline360 ApS. All rights reserved.",
      neverLeaves: "データがプラントの外に出ることはありません。",
      privacy: "プライバシーポリシー",
      terms: "利用規約",
      contact: "お問い合わせ",
    },
  },
  ...extra,
};
